import { request } from "hooks/useOkoApi/useOkoApi";

// The warm-up endpoints, reached only from flag-on surfaces.

export const fetchWarmUpSets = () => request("warm-up-sets", { method: "GET" });

// A class's warm-up assignments and its free codes, in one read.
export const fetchClassWarmUpGroups = (classroomId) =>
  request(`warm-up-groups/classrooms/${classroomId}`, { method: "GET" });

export const fetchFreeCodes = fetchClassWarmUpGroups;

// Sets the length of every group of the assignment; answers the assignment.
export const updateWarmUpSessionLength = (classroomId, assignmentId, sessionLength) =>
  request(`warm-up-groups/classrooms/${classroomId}/${assignmentId}`, {
    method: "PATCH",
    payload: { session_length: sessionLength },
  });

// Adds free codes to the assignment and releases some of its codes, in one
// change; answers the assignment.
export const updateWarmUpGroupCodes = (classroomId, assignmentId, { addGroupIds, removeGroupIds }) =>
  request(`warm-up-groups/classrooms/${classroomId}/${assignmentId}/groups`, {
    method: "PATCH",
    payload: { add_group_ids: addGroupIds, remove_group_ids: removeGroupIds },
  });

// Deletes the assignment and frees its codes: 204 (an empty body), or 404 once
// it is gone. The 204 is taken as is, so the DELETE is never sent twice.
export const archiveWarmUpAssignment = (classroomId, assignmentId) =>
  request(`warm-up-groups/classrooms/${classroomId}/${assignmentId}`, { method: "DELETE" });

// The free-codes read answers 404 for a class the teacher cannot reach and 422
// for an id that is not a UUID: both mean the page has no class to work on.
const UNREACHABLE_CLASS_STATUSES = [404, 422];

export const isUnreachableClass = (error) => UNREACHABLE_CLASS_STATUSES.includes(error?.status);

// Every class the teacher reaches, with the free codes a warm-up could claim.
export const fetchWarmUpFreeCodesByClass = () =>
  request("warm-up-groups/free-codes", { method: "GET" }).then((body) =>
    Object.fromEntries((body?.classes || []).map((c) => [c.classroom_id, c.free_codes]))
  );

// Copies the assignment into each target class, all or nothing; one key per
// opening of the picker, re-sent on a retry. Answers { copies }.
export const copyWarmUpAssignment = (classroomId, assignmentId, { targetClassroomIds, clientRequestId }) =>
  request(`warm-up-groups/classrooms/${classroomId}/${assignmentId}/copy`, {
    method: "POST",
    payload: { target_classroom_ids: targetClassroomIds, client_request_id: clientRequestId },
  });

export const fetchWarmUpSet = (setId) => request(`warm-up-sets/${setId}`, { method: "GET" });

export const createWarmUpGroups = (payload) => request("warm-up-groups", { method: "POST", payload });

// POST /warm-up-groups failures, by what the modal does about them.
const NOT_ENOUGH_CODES = "Not enough unused group codes in this classroom";
const KEY_ALREADY_USED = "client_request_id was already used for another request";

export const SAVE_FAILURE = {
  notEnoughCodes: "notEnoughCodes",
  keyReused: "keyReused",
  setUnavailable: "setUnavailable",
  classUnreachable: "classUnreachable",
  other: "other",
};

export const saveFailureOf = (error) => {
  if (error?.status === 409 && error.detail === NOT_ENOUGH_CODES) return SAVE_FAILURE.notEnoughCodes;
  if (error?.status === 409 && error.detail === KEY_ALREADY_USED) return SAVE_FAILURE.keyReused;
  if (error?.status === 400) return SAVE_FAILURE.setUnavailable;
  if (error?.status === 404) return SAVE_FAILURE.classUnreachable;
  return SAVE_FAILURE.other;
};
